import { useEffect, useState } from "react";
import { CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { formatARS } from "../../lib/site";

const POLICY = "Sin cargo hasta 48 h antes · 30% entre 48 y 24 h · 50% entre 24 y 2 h · menos de 2 h: se paga el 100% de nuevo.";

export const ChangeTrip = ({ code, t, autoOpen = false, onChanged }) => {
  const [d, setD] = useState(null);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const open = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.get(`/tickets/${encodeURIComponent(code)}/change-options`, { params: { t } });
      setD(data); setTarget(data?.options?.[0]?.id ?? "");
    } catch (e) { setMsg(apiError(e)); } finally { setBusy(false); }
  };
  const [auto, setAuto] = useState(autoOpen);
  useEffect(() => { if (auto) { setAuto(false); open(); } }, [auto]); // eslint-disable-line react-hooks/exhaustive-deps
  const fee = d?.fee?.amount ?? 0;
  const payNow = fee > 0 && d?.status === "confirmado";
  const go = async () => {
    const o = d?.options?.find((x) => x.id === target);
    if (!o || !window.confirm(`¿Cambiar al ${o.date} ${o.departure} hs${fee ? ` con un cargo de ${formatARS(fee)}` : " sin cargo"}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/tickets/${encodeURIComponent(code)}/change`, { target_trip_id: target }, { params: { t } });
      if (data?.pay && data.checkout_url) { window.location.assign(data.checkout_url); return; }
      toast.success("¡Listo! Cambiamos tu horario y te mandamos un email.");
      setD(null); onChanged?.();
    } catch (e) { setMsg(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="change-trip" className="rounded-2xl bg-white p-5 text-[var(--ink)]">
      <p className="flex items-center gap-2 font-bold"><CalendarClock className="h-5 w-5 text-[var(--forest)]" /> Cambiar de horario</p>
      {!d && <p className="mt-1 text-xs text-[var(--muted-ink)]">{POLICY}</p>}
      {!d && <button onClick={open} disabled={busy} data-testid="change-open" className="mt-3 inline-flex h-11 items-center gap-2 rounded-xl bg-[var(--ink)] px-4 text-sm font-bold text-white">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Ver horarios disponibles</button>}
      {d && (
        <div className="mt-3 space-y-3">
          <p data-testid="change-fee" className={`rounded-xl p-3 text-sm ${fee ? "bg-amber-100 text-amber-950" : "bg-[var(--lime)]/40"}`}>
            {d.fee?.reason}{fee ? ` · cargo ${formatARS(fee)}${d.status === "reservado" ? " (se suma a tu reserva y lo pagás en boletería)" : ""}` : ""}
          </p>
          {d.options?.length ? (
            <select data-testid="change-target" value={target} onChange={(e) => setTarget(e.target.value)} className="h-12 w-full min-w-0 rounded-xl border border-black/10 bg-white px-3">
              {d.options.map((o) => <option key={o.id} value={o.id}>{o.date} · {o.departure} hs{o.changed_seats ? " · otra butaca" : " · misma butaca"}</option>)}
            </select>
          ) : <p data-testid="change-empty" className="text-sm">No hay otros horarios con lugar en tu categoría. Consultá en boletería.</p>}
          {payNow && !d.card_enabled ? (
            <p data-testid="change-office" className="text-sm">Este cambio tiene cargo: acercate a cualquier boletería con tu código para pagarlo y hacer el cambio.</p>
          ) : d.options?.length > 0 && (
            <button onClick={go} disabled={busy || !target} data-testid="change-confirm" className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[var(--forest)] font-bold text-white disabled:opacity-40">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {payNow ? `Pagar ${formatARS(fee)} con tarjeta y cambiar` : "Confirmar cambio"}
            </button>
          )}
          {payNow && d.card_enabled && <p className="text-xs text-[var(--muted-ink)]">También podés pagar el cargo en cualquier boletería.</p>}
        </div>
      )}
      {msg && <p data-testid="change-error" className="mt-2 text-sm font-medium text-red-700">{msg}</p>}
    </div>
  );
};
